const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const clampTwoDigits = (value) => String(value).padStart(2, "0");

const cloneDate = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return null;
  }
  return new Date(value.getTime());
};

export const formatDateToISO = (input) => {
  const date = cloneDate(input instanceof Date ? input : new Date(input));
  if (!date) return null;

  const year = date.getFullYear();
  const month = clampTwoDigits(date.getMonth() + 1);
  const day = clampTwoDigits(date.getDate());

  return `${year}-${month}-${day}`;
};

export const parseISODate = (isoDate) => {
  if (!isoDate || typeof isoDate !== "string" || !ISO_DATE_REGEX.test(isoDate)) {
    return null;
  }

  const [year, month, day] = isoDate.split("-").map(Number);
  if ([year, month, day].some(Number.isNaN)) {
    return null;
  }

  // Create dates at noon to avoid DST/timezone shifts when rendering calendars.
  return new Date(year, month - 1, day, 12, 0, 0, 0);
};

export const normalizeToISODate = (value) => {
  if (!value) return null;

  if (typeof value === "string") {
    if (ISO_DATE_REGEX.test(value)) {
      return value;
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed) ? null : formatDateToISO(parsed);
  }

  if (value instanceof Date) {
    return Number.isNaN(value) ? null : formatDateToISO(value);
  }

  return null;
};

export const formatISODateForDisplay = (
  value,
  locale = "en-US",
  options = { weekday: "short", month: "short", day: "numeric", year: "numeric" }
) => {
  const iso = normalizeToISODate(value);
  const date = parseISODate(iso);
  if (!date) return "";
  return date.toLocaleDateString(locale, options);
};

export const getTodayISODate = () => formatDateToISO(new Date());

export const addDaysToISODate = (value, days) => {
  const baseIso = normalizeToISODate(value) || getTodayISODate();
  const date = parseISODate(baseIso);
  if (!date) return baseIso;
  date.setDate(date.getDate() + days);
  return formatDateToISO(date);
};

export const getUpcomingWeekendISODate = (value) => {
  const baseIso = normalizeToISODate(value) || getTodayISODate();
  const baseDate = parseISODate(baseIso);
  if (!baseDate) return baseIso;

  const dayOfWeek = baseDate.getDay();
  const daysUntilSaturday = ((6 - dayOfWeek + 7) % 7) || 7;

  baseDate.setDate(baseDate.getDate() + daysUntilSaturday);
  return formatDateToISO(baseDate);
};

export const ensureISODate = (value, fallback = getTodayISODate()) =>
  normalizeToISODate(value) || fallback;
